import { Entity, IEntity, filterNil } from '@lycoris-nubila/ngx-lycoris';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { NotificationType } from 'application/notification/model/notification.type';
import { Document, IDocument } from 'document/document';
import { IModule, Module } from 'module/module';
import { TaskType } from 'task/model/task.type';
import { ITask, Task } from 'task/task';
import { IUser, User } from 'user/user';

import { NotificationDisplay } from './notification-display';
import {
	INotificationMeeting,
	NotificationMeeting,
} from './notification-meeting';

export interface INotification extends IEntity {
	readonly taskId: string;
	readonly creation: Date;
	readonly moduleId: string;
	readonly documentId: string;
	readonly sourceUserId: string;
	readonly targetUserId: string;
	readonly type: NotificationType;
	readonly contactsName: string[];
	readonly meeting: INotificationMeeting;
	readonly task$: Observable<ITask>;
	readonly module$: Observable<IModule>;
	readonly sourceUser$: Observable<IUser>;
	readonly targetUser$: Observable<IUser>;
	readonly document$: Observable<IDocument>;

	getDisplay$(user: IUser): Observable<NotificationDisplay>;

	isSource(user: IUser): boolean;

	isClosable(): boolean;
}

export class Notification extends Entity implements INotification {
	private _taskId: string;
	private _creation: Date;
	private _moduleId: string;
	private _documentId: string;
	private _sourceUserId: string;
	private _targetUserId: string;
	private _type: NotificationType;
	private _contactsName: string[];
	private _meeting: NotificationMeeting;
	private _task$: Observable<Task>;
	private _module$: Observable<Module>;
	private _sourceUser$: Observable<User>;
	private _targetUser$: Observable<User>;
	private _document$: Observable<Document>;

	constructor(
		id: string,
		taskId: string,
		creation: Date,
		moduleId: string,
		documentId: string,
		sourceUserId: string,
		targetUserId: string,
		type: NotificationType,
		contactsName: string[],
		updateTimestamp: number,
		meeting: NotificationMeeting,
		task$: Observable<Task> = of(null),
		module$: Observable<Module> = of(null),
		sourceUser$: Observable<User> = of(null),
		targetUser$: Observable<User> = of(null),
		document$: Observable<Document> = of(null)
	) {
		super(id, null, updateTimestamp);
		this._taskId = taskId;
		this._creation = creation;
		this._moduleId = moduleId;
		this._documentId = documentId;
		this._sourceUserId = sourceUserId;
		this._targetUserId = targetUserId;
		this._type = type;
		this._contactsName = contactsName;
		this._meeting = meeting;
		this._task$ = task$;
		this._module$ = module$;
		this._sourceUser$ = sourceUser$;
		this._targetUser$ = targetUser$;
		this._document$ = document$;
	}

	public get taskId(): string {
		return this._taskId;
	}

	public get documentId(): string {
		return this._documentId;
	}

	public get creation(): Date {
		return this._creation;
	}

	public get moduleId(): string {
		return this._moduleId;
	}

	public get meeting(): NotificationMeeting {
		return this._meeting;
	}

	public get sourceUserId(): string {
		return this._sourceUserId;
	}

	public get targetUserId(): string {
		return this._targetUserId;
	}

	public get type(): NotificationType {
		return this._type;
	}

	public get contactsName(): string[] {
		return this._contactsName;
	}

	public get task$(): Observable<Task> {
		return this._task$;
	}

	public get module$(): Observable<Module> {
		return this._module$;
	}

	public get sourceUser$(): Observable<User> {
		return this._sourceUser$;
	}

	public get targetUser$(): Observable<User> {
		return this._targetUser$;
	}

	public get document$(): Observable<Document> {
		return this._document$;
	}

	public getDisplay$(user: IUser): Observable<NotificationDisplay> {
		switch (this._type) {
			case NotificationType.DOCUMENT_ADDED:
			case NotificationType.BASIC_ASSESSMENT_DOCUMENT_GENERATED:
			case NotificationType.DETAILED_ASSESSMENT_DOCUMENT_GENERATED:
				return of({
					icon: 'fas fa-file',
					actionIcon: 'far fa-eye',
					title: 'presentation.app.common.notification.document-added.title',
					subtitle: 'presentation.app.common.notification.document-added.subtitle',
				});
			case NotificationType.MODULE_DONE:
				if (this.sourceUserId) {
					return of({
						icon: 'fas fa-cube',
						actionIcon: 'far fa-eye',
						title: 'presentation.app.common.notification.module-done.title',
						subtitle: 'presentation.app.common.notification.module-done.subtitle',
					});
				} else {
					return of({
						icon: 'fas fa-cube',
						actionIcon: 'far fa-eye',
						title: 'presentation.app.common.notification.module-rate.title',
						subtitle: 'presentation.app.common.notification.module-rate.subtitle',
					});
				}
			case NotificationType.MODULE_STARTED:
				return of({
					icon: 'fas fa-cube',
					actionIcon: 'far fa-eye',
					title: 'presentation.app.common.notification.module-started.title',
					subtitle: 'presentation.app.common.notification.module-started.subtitle',
				});
			case NotificationType.MODULE_COMMENTED:
				return of({
					icon: 'fas fa-cube',
					actionIcon: 'far fa-eye',
					title: 'presentation.app.common.notification.module-commented.title',
					subtitle: 'presentation.app.common.notification.module-commented.subtitle',
				});
			case NotificationType.MODULE_PUSHES_ADDED:
				return of({
					icon: 'fas fa-at',
					actionIcon: null,
					title: 'presentation.app.common.notification.module-pushes-added.title',
					subtitle:
						'presentation.app.common.notification.module-pushes-added.subtitle',
				});
			case NotificationType.MEETING_REQUEST:
				return user?.id === this._sourceUserId
					? of({
							actionIcon: 'far fa-trash-alt',
							icon: 'far fa-calendar-alt',
							title:
								'presentation.app.common.notification.meeting-request.source.title',
							subtitle:
								'presentation.app.common.notification.meeting-request.source.subtitle',
					  })
					: of({
							actionIcon: 'fas fa-reply',
							icon: 'far fa-calendar-alt',
							title:
								'presentation.app.common.notification.meeting-request.target.title',
							subtitle:
								'presentation.app.common.notification.meeting-request.target.subtitle',
					  });
			case NotificationType.MODULE_ADDED:
				return of({
					icon: 'fas fa-cube',
					actionIcon: 'far fa-eye',
					title: 'presentation.app.common.notification.module-added.title',
					subtitle: 'presentation.app.common.notification.module-added.subtitle',
				});
			case NotificationType.MEETING_ALTERNATIVE:
				return user?.id === this._sourceUserId
					? of({
							actionIcon: 'far fa-trash-alt',
							icon: 'far fa-calendar-alt',
							title:
								'presentation.app.common.notification.meeting-alternative.source.title',
							subtitle:
								'presentation.app.common.notification.meeting-alternative.source.subtitle',
					  })
					: of({
							actionIcon: 'fas fa-reply',
							icon: 'far fa-calendar-alt',
							title:
								'presentation.app.common.notification.meeting-alternative.target.title',
							subtitle:
								'presentation.app.common.notification.meeting-alternative.target.subtitle',
					  });
			case NotificationType.MEETING_ACCEPTANCE:
				return of({
					actionIcon: null,
					icon: 'far fa-check-circle',
					title: 'presentation.app.common.notification.meeting-acceptance.title',
					subtitle:
						'presentation.app.common.notification.meeting-acceptance.subtitle',
				});
			case NotificationType.MEETING_REJECT:
				return of({
					actionIcon: null,
					icon: 'far fa-times-circle',
					title: 'presentation.app.common.notification.meeting-reject.title',
					subtitle: 'presentation.app.common.notification.meeting-reject.subtitle',
				});
			case NotificationType.COACH_ASSOCIATION:
				return of({
					actionIcon: null,
					icon: 'fas fa-user-friends',
					title: 'presentation.app.common.notification.coach-association.title',
					subtitle:
						'presentation.app.common.notification.coach-association.subtitle',
				});
			case NotificationType.TASK_ADDED:
				return this.task$.pipe(
					filterNil(),
					map((task) => {
						switch (task.type) {
							case 'MODULE':
								return {
									actionIcon: null,
									icon: 'fas fa-tasks',
									title: 'presentation.app.common.notification.task-added.module.title',
									subtitle:
										'presentation.app.common.notification.task-added.module.subtitle',
								};
							case 'JOB_OFFER':
								return {
									actionIcon: null,
									icon: task.typeCssIconClass,
									title: task.todoTitle,
									subtitle:
										'presentation.app.common.notification.task-added.job-offer.subtitle',
								};
							case 'MEETING':
								return {
									actionIcon: null,
									icon: 'fas fa-tasks',
									title: 'presentation.app.common.notification.task-added.meeting.title',
									subtitle:
										'presentation.app.common.notification.task-added.meeting.subtitle',
								};
							case 'CONTACT':
								return {
									actionIcon: null,
									icon: 'fas fa-tasks',
									title: 'presentation.app.common.notification.task-added.contact.title',
									subtitle:
										'presentation.app.common.notification.task-added.contact.subtitle',
								};
							default:
								return {
									actionIcon: null,
									icon: 'fas fa-tasks',
									title: 'presentation.app.common.notification.task-added.common.title',
									subtitle:
										'presentation.app.common.notification.task-added.common.subtitle',
								};
						}
					})
				);
			case NotificationType.TASK_UPDATED:
				return this.task$.pipe(
					filterNil(),
					map((task) => {
						switch (task.type) {
							case 'MODULE':
								return {
									actionIcon: null,
									icon: task.typeCssIconClass,
									title: task.done
										? 'presentation.app.common.notification.task-done.module.title'
										: 'presentation.app.common.notification.task-updated.module.title',
									subtitle:
										'presentation.app.common.notification.task-updated.module.subtitle',
								};
							case 'MEETING':
								return {
									actionIcon: null,
									icon: task.typeCssIconClass,
									title: task.done
										? 'presentation.app.common.notification.task-done.meeting.title'
										: 'presentation.app.common.notification.task-updated.meeting.title',
									subtitle:
										'presentation.app.common.notification.task-updated.meeting.subtitle',
								};
							case 'JOB_OFFER':
								return {
									actionIcon: null,
									icon: task.typeCssIconClass,
									title: task.todoTitle,
									subtitle:
										'presentation.app.common.notification.task-updated.job-offer.subtitle',
								};
							case 'CONTACT':
								return {
									actionIcon: null,
									icon: task.typeCssIconClass,
									title: task.done
										? 'presentation.app.common.notification.task-done.contact.title'
										: 'presentation.app.common.notification.task-updated.contact.title',
									subtitle:
										'presentation.app.common.notification.task-updated.contact.subtitle',
								};
							default:
								return {
									actionIcon: null,
									icon: task.typeCssIconClass,
									title: task.done
										? 'presentation.app.common.notification.task-done.common.title'
										: 'presentation.app.common.notification.task-updated.common.title',
									subtitle:
										'presentation.app.common.notification.task-updated.common.subtitle',
								};
						}
					})
				);
			default:
				return of({
					actionIcon: null,
					icon: 'far fa-check-circle',
					title: 'presentation.app.common.notification.default.title',
					subtitle: 'presentation.app.common.notification.default.subtitle',
				});
		}
	}

	public isClosable(): boolean {
		switch (this._type) {
			case NotificationType.MEETING_REQUEST:
			case NotificationType.MEETING_ALTERNATIVE:
				return false;
			default:
				return true;
		}
	}

	public isSource(user: IUser): boolean {
		return user.id === this._sourceUserId;
	}
}
